import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { Helmet } from 'react-helmet';

import { RootState } from '../../../ducks/store';
import { clearReportData, fetchCLEReport } from '../../../ducks/report';
import PageHeader from '../../common/PageHeader/PageHeader';
import Report from '../../common/Report/Report';
import OverlayProgressBar from '../../common/OverlayProgressBar/OverlayProgressBar';
import { useBatchedReport, BatchFetchFn } from '../../../hooks/useBatchedReport';
import { useOverlayTransition } from '../../../hooks/useOverlayTransition';

import './CLEReport.scss';

const getLastMonthDates = () => {
  const today = new Date();
  const month = today.getMonth();
  const firstLastMonth = new Date();
  firstLastMonth.setMonth(month - 1);
  firstLastMonth.setDate(1);

  const lastLastMonth = new Date();
  lastLastMonth.setDate(0);
  return [firstLastMonth, lastLastMonth];
};

const CLEReport = () => {
  const dispatch = useDispatch();

  const { user, batchIsLoading, batchProgress, batchError, data } = useSelector(
    (state: RootState) => ({
      user: state.auth.authenticatedUser,
      batchIsLoading: state.report.batchIsLoading,
      batchProgress: state.report.batchProgress,
      batchError: state.report.batchError,
      data: state.report.data,
    }),
  );

  const [firstLastMonth, lastLastMonth] = getLastMonthDates();
  const [fromDate, setFromDate] = useState<Date | null>(firstLastMonth);
  const [toDate, setToDate] = useState<Date | null>(lastLastMonth);

  // Single-batch key — CLE doesn't batch by team, just a single request
  const [batchKey, setBatchKey] = useState<string>('initial');

  const fetchFn: BatchFetchFn = useCallback(
    async (_key: string, signal: AbortSignal) => {
      if (!fromDate || !toDate) return { rows: [] };
      return fetchCLEReport(fromDate, toDate, signal);
    },
    [fromDate, toDate],
  );

  const { trigger, abort } = useBatchedReport({
    batchKeys: [batchKey],
    fetchFn,
  });

  // Fetch on mount
  useEffect(() => {
    trigger();
    return () => {
      abort();
      dispatch(clearReportData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [from, to] = dates;
    setFromDate(from);
    setToDate(to);

    if (from && to) {
      abort();
      // Change the batch key to trigger a new fetch
      setBatchKey(`${from.toISOString()}-${to.toISOString()}`);
    }
  };

  // Trigger fetch when batchKey changes (after date range selection)
  useEffect(() => {
    if (batchKey !== 'initial') {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchKey]);

  const {
    showOverlay,
    isComplete,
    handleComplete: handleOverlayComplete,
  } = useOverlayTransition(batchIsLoading);

  return (
    <div className="CLEReport d-flex flex-column">
      <Helmet>
        <title>CLE Report - Praktio</title>
      </Helmet>
      <PageHeader pageName="CLE Report" className="mb-0">
        <div>Date Range</div>
        <DatePicker
          selected={fromDate}
          onChange={onDateRangeChange}
          startDate={fromDate}
          endDate={toDate}
          placeholderText="No date range selected."
          selectsRange
        />
      </PageHeader>
      {showOverlay && (
        <OverlayProgressBar
          progress={batchProgress}
          isComplete={isComplete}
          onCancel={abort}
          onComplete={handleOverlayComplete}
        />
      )}
      {batchError && (
        <div>
          Oh no! Something went wrong. <br /> {batchError}
        </div>
      )}
      {!showOverlay && !batchError && <Report user={user} reportData={data} isCLE />}
    </div>
  );
};

export default CLEReport;
